import {
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  Input,
} from '@chakra-ui/react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FaHospital } from 'react-icons/fa';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { usePermissions } from '../../../hooks/usePermissions';
import { postUnifiedSearch } from '../../../services/unified-search/unified-search';
import { Practice } from '../../../types';

interface PracticeSearchSelectProps {
  field: {
    name: string;
    value?: string | { label: string; value: string }[];
    onChange: (
      value:
        | { label: string; value: string }
        | { label: string; value: string }[]
    ) => void;
  };
  placeholder?: string;
  isInvalid?: boolean;
  /** Identifies this instance when several searches share one form. */
  testId?: string;
}

interface Option {
  label: string;
  value: string;
}

const MIN_SEARCH_CHARS = 2;
const PAGE_SIZE = 10;

const PracticeSearchSelect: FC<PracticeSearchSelectProps> = ({
  field,
  placeholder = 'Enter practice name…',
  isInvalid = false,
  testId = 'practice-search',
}) => {
  const { permissions } = usePermissions();
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState<Option[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // Cursor token for the next page; null means no further pages exist
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  // Keep last query so "Load more" can re-use it
  const lastQueryRef = useRef<string>('');
  // Track loaded IDs to detect duplicate/end-of-results pages
  const loadedIdsRef = useRef<Set<string>>(new Set());
  // Monotonic id of the most recent request; responses from older requests
  // are ignored so a slow first search cannot overwrite a newer one
  const requestIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback(async () => {
    const query = searchInput.trim();
    if (query.length < MIN_SEARCH_CHARS) return;

    setIsSearching(true);
    setHasError(false);
    setHasSearched(true);
    setIsOpen(false);
    setResults([]);
    setNextPageToken(null);
    // A pending "Load more" is superseded by this search and will skip its own
    // cleanup, so clear its loading flag here to avoid a stuck spinner
    setIsLoadingMore(false);
    lastQueryRef.current = query;
    const requestId = ++requestIdRef.current;

    try {
      const response = await postUnifiedSearch(
        query,
        PAGE_SIZE,
        undefined,
        undefined,
        permissions.isAdmin
      );
      const options: Option[] = response.data.practices.map(
        (practice: Practice) => ({
          label: practice.name,
          value: practice.id,
        })
      );
      if (requestId !== requestIdRef.current) return; // superseded
      const token = response.data.practiceNextToken;
      loadedIdsRef.current = new Set(options.map((o) => o.value));
      setResults(options);
      setNextPageToken(token && token.trim() !== '' ? token : null);
      setIsOpen(true);
    } catch {
      if (requestId !== requestIdRef.current) return; // superseded
      setHasError(true);
      setResults([]);
      setIsOpen(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsSearching(false);
      }
    }
  }, [searchInput, permissions.isAdmin]);

  const handleLoadMore = useCallback(async () => {
    if (!nextPageToken || !lastQueryRef.current) return;

    setIsLoadingMore(true);
    const requestId = ++requestIdRef.current;
    try {
      const response = await postUnifiedSearch(
        lastQueryRef.current,
        PAGE_SIZE,
        nextPageToken,
        undefined,
        permissions.isAdmin
      );
      const moreOptions: Option[] = response.data.practices.map(
        (practice: Practice) => ({
          label: practice.name,
          value: practice.id,
        })
      );
      if (requestId !== requestIdRef.current) return; // superseded
      const token = response.data.practiceNextToken;
      const newItems = moreOptions.filter((o) => !loadedIdsRef.current.has(o.value));
      if (newItems.length === 0) {
        // No unique results returned — we've reached the real end
        setNextPageToken(null);
      } else {
        newItems.forEach((o) => loadedIdsRef.current.add(o.value));
        setResults((prev) => [...prev, ...newItems]);
        setNextPageToken(token && token.trim() !== '' ? token : null);
      }
    } catch {
      // Non-fatal — keep existing results visible; user can retry
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [nextPageToken, permissions.isAdmin]);

  const handleSelect = (option: Option) => {
    field.onChange({ label: option.label, value: option.value });
    setSearchInput('');
    setResults([]);
    setIsOpen(false);
    setHasSearched(false);
    setNextPageToken(null);
    lastQueryRef.current = '';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const isSearchDisabled = searchInput.trim().length < MIN_SEARCH_CHARS;
  const showLoadMore = Boolean(nextPageToken);

  return (
    <Box
      ref={containerRef}
      data-testid={testId}
      w="100%"
    >
      <HStack spacing={3} align="stretch">
        {/* Input with leading search icon */}
        <Box position="relative" flex={1}>
          <Box
            position="absolute"
            left="0.875rem"
            top="50%"
            transform="translateY(-50%)"
            color={isInvalid ? 'red.400' : 'gray.400'}
            pointerEvents="none"
            zIndex={1}
          >
            <FiSearch size={16} />
          </Box>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            pl="2.625rem"
            h="2.875rem"
            fontSize="sm"
            isInvalid={isInvalid && !isOpen}
            borderColor={isInvalid && !isOpen ? 'red.400' : 'gray.300'}
            _placeholder={{ color: 'gray.400', fontSize: 'sm' }}
            _focus={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
            }}
            _hover={{ borderColor: 'gray.400' }}
          />
        </Box>

        {/* Search button */}
        <Button
          onClick={handleSearch}
          isLoading={isSearching}
          loadingText="Searching…"
          isDisabled={isSearchDisabled}
          colorScheme="blue"
          h="2.875rem"
          px={6}
          flexShrink={0}
          fontSize="sm"
          fontWeight={600}
        >
          Search
        </Button>
      </HStack>

      {/* Too-short hint */}
      {searchInput.length > 0 && isSearchDisabled && (
        <Text fontSize="xs" color="gray.500" mt={1.5} ml={1}>
          Enter at least {MIN_SEARCH_CHARS} characters to search
        </Text>
      )}

      {/* Results dropdown */}
      {isOpen && (
        <Box
          mt={1.5}
          bg="white"
          border="1px solid"
          borderColor="blue.100"
          borderRadius="lg"
          boxShadow="0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)"
          overflow="hidden"
        >
          {hasError ? (
            <Box px={4} py={3} bg="red.50">
              <Text fontSize="sm" color="red.600" fontWeight={500}>
                Something went wrong. Please try again.
              </Text>
            </Box>
          ) : results.length === 0 ? (
            <Box px={4} py={4} textAlign="center">
              <Box
                color="gray.300"
                mb={2}
                display="flex"
                justifyContent="center"
              >
                <FaHospital size={22} />
              </Box>
              <Text fontSize="sm" color="gray.500" fontWeight={500}>
                No practices found
              </Text>
              <Text fontSize="xs" color="gray.400" mt={0.5}>
                Try a different name or partial match
              </Text>
            </Box>
          ) : (
            <>
              {/* Result count header */}
              <Box
                px={4}
                py={2}
                borderBottom="1px solid"
                borderColor="gray.100"
                bg="gray.50"
              >
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight={600}
                  letterSpacing="0.03em"
                >
                  {results.length} PRACTICE{results.length !== 1 ? 'S' : ''}
                  {showLoadMore ? ' · MORE AVAILABLE' : ' FOUND'}
                </Text>
              </Box>

              {/* Scrollable result list */}
              <Box maxH="15rem" overflowY="auto" role="listbox">
                {results.map((option) => (
                  <Box
                    as="button"
                    type="button"
                    key={option.value}
                    w="100%"
                    px={4}
                    py={3}
                    cursor="pointer"
                    _hover={{ bg: 'blue.50' }}
                    _focus={{ bg: 'blue.50', outline: 'none', boxShadow: 'inset 0 0 0 2px var(--chakra-colors-blue-400)' }}
                    borderBottom="1px solid"
                    borderColor="gray.50"
                    onClick={() => handleSelect(option)}
                    role="option"
                    aria-selected={false}
                    transition="background-color 0.12s ease"
                    _last={{ borderBottom: 'none' }}
                  >
                    <HStack spacing={3} align="center">
                      <Box
                        color="blue.400"
                        flexShrink={0}
                        w="1.75rem"
                        h="1.75rem"
                        bg="blue.50"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <FaHospital size={12} />
                      </Box>
                      <Text
                        fontSize="sm"
                        color="gray.800"
                        fontWeight={500}
                        lineHeight="short"
                      >
                        {option.label}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </Box>

              {/* Load more footer — only shown when another page exists */}
              {showLoadMore && (
                <Box
                  px={4}
                  py={2.5}
                  borderTop="1px solid"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  {isLoadingMore ? (
                    <HStack justify="center" spacing={2} py={0.5}>
                      <Spinner size="xs" color="blue.400" />
                      <Text fontSize="xs" color="gray.500">
                        Loading more…
                      </Text>
                    </HStack>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      w="100%"
                      color="blue.500"
                      fontWeight={600}
                      fontSize="xs"
                      leftIcon={<FiChevronDown size={13} />}
                      _hover={{ bg: 'blue.50', color: 'blue.600' }}
                      onClick={handleLoadMore}
                    >
                      Load more results
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {hasSearched && !isOpen && !hasError && !isSearching && results.length === 0 && (
        <Text fontSize="xs" color="gray.500" mt={1.5} ml={1}>
          No practices found. Try a different search term.
        </Text>
      )}
    </Box>
  );
};

export default PracticeSearchSelect;
